import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find(a => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl" />
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/category/all"
              className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 inline-block"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const currentCategory = categories.find(c => c.slug === article.category);
  const categoryName = currentCategory ? currentCategory.name : '未分类';

  const relatedArticles = articles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${article.title} - 91n.com`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://91n.com/article/${article.slug}`}
        ogType="article"
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
            { '@type': 'ListItem', position: 2, name: categoryName, item: `https://91n.com/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://91n.com/article/${article.slug}` },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: '91n.com', url: 'https://91n.com' },
          url: `https://91n.com/article/${article.slug}`,
          image: article.image,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100/60 border-b border-background-200/50">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-6">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {article.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" /> {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" /> {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" /> 阅读 {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[400px] object-cover object-top rounded-lg"
              />
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
                prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                prose-strong:text-foreground-900
                [&_h2]:text-xl [&_h2]:font-bold [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-base [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/60 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-8">
                相关<strong className="text-primary-500">推荐</strong>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/50 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" /> {item.readTime}
                        </span>
                        <span>{item.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}